/**
 * Birthday Input Component
 *
 * Collects date of birth.
 * LoadingBar and navigation buttons are rendered by the parent layout.
 */

"use client";

import { DateStorefrontInput } from "@frontend/components/input/DateStorefrontInput";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@frontend/shared/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useForm } from "react-hook-form";
import {
  useInsuranceConnectorContext,
  useInsuranceConnectorSend,
} from "../insurance-connector-provider";
import {
  type BirthdayFormData,
  birthdaySchema,
} from "../machine/schemas/validation.schemas";
import { INSURANCE_STEP_FORM_ID } from "../step-config";

export function BirthdayInput() {
  const context = useInsuranceConnectorContext();
  const send = useInsuranceConnectorSend();
  const t = useTranslations("InsuranceConnector");

  const form = useForm<BirthdayFormData>({
    resolver: zodResolver(birthdaySchema),
    defaultValues: {
      birthday: context.birthday || "",
    },
  });

  const onSubmit = (data: BirthdayFormData) => {
    send({ type: "SUBMIT", data });
  };

  return (
    <Form {...form}>
      <form
        id={INSURANCE_STEP_FORM_ID}
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-4 md:gap-8"
      >
        <h2 className="text-2xl md:text-[32px] font-semibold flex flex-col md:space-y-0 md:leading-tight">
          <span>{t("HiFirstName", { firstName: context.firstName })}</span>
          <span>{t("WhenWereYouBorn")}</span>
        </h2>
        <FormField
          control={form.control}
          name="birthday"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel className="sr-only">{t("DateOfBirth")}</FormLabel>
              <FormControl>
                <DateStorefrontInput
                  className="rounded-full py-5 text-lg w-full px-5 border-transparent focus:border-transparent focus:ring-1 focus:ring-[#00A3FF] focus:outline-none focus-visible:ring-1 focus-visible:ring-[#00A3FF] focus-visible:ring-offset-0"
                  autoFocus
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
